
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/features/shared/components/ui/tabs';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { PossibleDuplicatesCard } from './components/PossibleDuplicatesCard';
import { useLocation } from 'react-router-dom';
import { EmployeeDirectory } from './components/EmployeeDirectory';
import { OrganizationalChart } from './components/OrganizationalChart';
import { OrganizationView } from './components/OrganizationView';
import { AIEmployeeSearch } from './components/AIEmployeeSearch';
import { MobileEmployeeDirectory } from './components/MobileEmployeeDirectory';
import { DataReadinessSummaryCard } from './components/DataReadinessSummaryCard';

export const EmployeesPage: React.FC = () => {
  const { t } = useTranslation(['employees']);
  const [activeTab, setActiveTab] = useState('directory');
  const { isManager, isTenantAdmin, isEmployee, canManageEmployees: canWriteEmployees } = useRoleAccess();
  // Same derivation the detail page uses, so links work under both
  // /dashboard/:slug and /t/:slug/app route shapes.
  const location = useLocation();
  const employeesBase = location.pathname.replace(/\/employees(\/.*)?$/, '/employees');

  const canManageEmployees = isManager || isTenantAdmin;

  // Different tab configurations based on role
  const getTabsConfig = () => {
    if (isEmployee && !isManager) {
      // Regular employee tabs
      return [
        { value: 'directory', label: t('employees:tabs.directory') },
        { value: 'discover', label: t('employees:tabs.ai_discovery') },
        { value: 'org-chart', label: t('employees:tabs.org_chart') },
      ];
    } else {
      // Manager/Admin tabs
      return [
        { value: 'directory', label: t('employees:tabs.management') },
        { value: 'my-org', label: t('employees:tabs.my_org', 'My Organization') },
        { value: 'discover', label: t('employees:tabs.ai_search') },
        { value: 'org-chart', label: t('employees:tabs.org_chart') },
      ];
    }
  };

  const tabsConfig = getTabsConfig();

  const getPageTitle = () => {
    if (isEmployee && !isManager) {
      return t('employees:title_employee');
    }
    return t('employees:title_manager');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-h1 bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">{getPageTitle()}</h1>
      </div>

      {/* Data readiness (Increment B3) — tenant-wide master-data completeness
          for HR/admins with employee:write. */}
      {canWriteEmployees && <DataReadinessSummaryCard />}

      {/* Duplicates already in the directory. Renders nothing when there are
          none, so it is a signal rather than permanent furniture. */}
      {canWriteEmployees && <PossibleDuplicatesCard employeesBase={employeesBase} />}

      {/* Tabs are shown on ALL sizes (the list is flex-wrap, so it stacks on
          mobile). Only the directory tab swaps to a mobile-optimised list. */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        {/* Flex-wrap (not a dynamic `grid-cols-N` class — Tailwind's JIT can't
            generate interpolated class names, which collapsed the bar). This
            lays the tabs out evenly and wraps to a second row when needed. */}
        <TabsList className="flex flex-wrap w-full h-auto gap-1 bg-orange-50 dark:bg-orange-500/[0.08] border border-orange-200 dark:border-orange-500/25 p-1">
          {tabsConfig.map(tab => (
            <TabsTrigger key={tab.value} value={tab.value} className="flex-1 min-w-[120px]">
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="directory" className="space-y-6">
          {/* Mobile-optimised list on phones, full table on desktop. */}
          <div className="md:hidden">
            <MobileEmployeeDirectory />
          </div>
          <div className="hidden md:block">
            <EmployeeDirectory />
          </div>
        </TabsContent>

        <TabsContent value="discover" className="space-y-6">
          <AIEmployeeSearch />
        </TabsContent>

        <TabsContent value="org-chart" className="space-y-6">
          <OrganizationalChart />
        </TabsContent>

        {canManageEmployees && (
          <TabsContent value="my-org" className="space-y-6">
            <OrganizationView />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
